import { getEnvironment, getEnvironmentValue, ENVIRONMENT_VALUES } from '@shared/utils';
import type { Result, AppError } from '../../error/types';
import { ok, err } from '../../error/functions';
import { ENVIRONMENTS } from '../../core/constants/ENVIRONMENTS';

/**
 * @group Functions
 * @summary <blockquote>Gets the base URL for the application based on the current environment using the Result pattern.</blockquote>
 * 
 * @remarks
 * This function determines the appropriate base URL based on the current environment.
 * It uses the centralized environment detection and returns a Result type for proper error handling.
 * 
 * @returns Result containing the base URL for the application or an AppError if URL determination fails
 * 
 * @example
 * ```typescript
 * import { getAppBaseUrl } from '@shared/utils';
 * 
 * const result = getAppBaseUrl();
 * 
 * result.match({
 *   ok: (baseUrl) => {
 *     console.log(`Base URL: ${baseUrl}`);
 *   },
 *   err: (error) => {
 *     console.error(`URL determination failed: ${error.errorMessage}`);
 *   }
 * });
 * ```
 * 
 * @example
 * ```typescript
 * // Environment-based URL resolution:
 * // Production: Requires VERCEL_PROJECT_PRODUCTION_URL or returns error
 * // Preview: Requires VERCEL_BRANCH_URL or returns error
 * // Development: Uses NEXTAUTH_URL or defaults to http://localhost:3000
 * ```
 */
export function getAppBaseUrl(): Result<string, AppError> {
  const environmentResult = getEnvironment();
  
  return environmentResult.match({
    ok: (environment) => {
      switch (environment) {
        case ENVIRONMENTS.PRODUCTION: {
          const productionUrlResult = getEnvironmentValue(ENVIRONMENT_VALUES.LOGGING.VERCEL_PROJECT_PRODUCTION_URL, true);
          return productionUrlResult.match({
            ok: (url: string) => ok(url),
            err: () => err({
              errorCode: 'MISSING_PRODUCTION_URL',
              errorMessage: 'VERCEL_PROJECT_PRODUCTION_URL environment variable is required for production environment'
            })
          });
        }
        case ENVIRONMENTS.PREVIEW: {
          const previewUrlResult = getEnvironmentValue(ENVIRONMENT_VALUES.LOGGING.VERCEL_BRANCH_URL, true);
          return previewUrlResult.match({
            ok: (url: string) => ok(url),
            err: () => err({
              errorCode: 'MISSING_PREVIEW_URL',
              errorMessage: 'VERCEL_BRANCH_URL environment variable is required for preview environment'
            })
          });
        }
        case ENVIRONMENTS.DEVELOPMENT: {
          const nextAuthUrlResult = getEnvironmentValue(ENVIRONMENT_VALUES.AUTH.NEXTAUTH_URL);
          return nextAuthUrlResult.match({
            ok: (url) => ok(url),
            err: () => ok('http://localhost:3000')
          });
        }
        default:
          return err({
            errorCode: 'UNSUPPORTED_ENVIRONMENT',
            errorMessage: `Unsupported environment for URL resolution: ${environment}`
          });
      }
    },
    err: (error) => {
      return err({
        errorCode: 'ENVIRONMENT_DETECTION_FAILED',
        errorMessage: `Failed to determine base URL due to environment detection error: ${error.errorMessage}`
      });
    }
  });
} 